import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { createRoomStingEngine } from "@/lib/roomStings";

// Curated set of themed rooms. Each maps to a real, filterable destination
// in the shop. Rooms are laid out in an asymmetric bento so the villa feels
// like a floor plan rather than a menu.
//
// `bg` is either a photo URL or a CSS-only background string — used when we
// don't have a licensed photo that matches the room's aesthetic tightly.
const ROOMS = [
  {
    key: "bedroom",
    name: "The Bedroom",
    sub: "Ocean-view suite · Lingerie",
    tagline: "Silk. Lace. First candlelight.",
    to: "/shop/lingerie",
    // Gemini-generated gothic bedroom — headboard in foreground, foot of bed
    // facing outward-thrown gothic arch doors that frame a huge Aegean moon
    img: "/api/files/ramshack/products/a4670b5cf450412ea272c744e7b14bfb.png",
    size: "md",
    accent: "rgba(200,90,110,0.35)",
  },
  {
    key: "basement",
    name: "The Basement",
    sub: "Behind the iron door · Discipline",
    tagline: "Cuffs, floggers, and slow instruction.",
    to: "/shop/basement",
    // Gemini-generated candle-lit stone staircase descending to a low altar
    img: "/api/files/ramshack/products/cb12e41eca0643e8b8f398c19a6f0cec.png",
    size: "md",
    accent: "rgba(120,10,20,0.55)",
  },
  {
    key: "salon",
    name: "The Salon",
    sub: "Sculpture parlor · Pleasure objects",
    tagline: "Curated toys, whisper-quiet.",
    to: "/shop/toys",
    // Gemini-generated gothic salon — velvet fainting couches, iron
    // chandelier, oil portraits, live fire in carved black-wood mantel
    img: "/api/files/ramshack/products/cee6fe3028eb431495d603022274e5ba.png",
    size: "md",
    accent: "rgba(210,180,140,0.35)",
  },
  {
    key: "apothecary",
    name: "The Apothecary",
    sub: "Wellness cabinet · Elixirs & oils",
    tagline: "Salves, arousal balms, aftercare.",
    to: "/shop/wellness",
    // Gemini-generated gothic apothecary — twin cathedral windows, moonlight
    // through iron tracery, floor-to-ceiling potion shelves warmly backlit,
    // brass alchemical scale on the counter
    img: "/api/files/ramshack/products/422ff9ca941c48a19d1efc8cfeee3e36.png",
    size: "md",
    accent: "rgba(190,120,60,0.35)",
  },
  {
    key: "parlor",
    name: "The Parlor",
    sub: "Card table & mischief · Games",
    tagline: "Party games, deeper questions.",
    to: "/shop/games",
    // Gemini-generated gothic parlor — tarot laid on wine-velvet round table,
    // throne chairs, gargoyle mantel, warm fireplace, gothic bookcase
    img: "/api/files/ramshack/products/053d59ba4c91499185bf61faa8aeda90.png",
    size: "md",
    accent: "rgba(100,20,30,0.5)",
  },
  {
    key: "atelier",
    name: "The Atelier",
    sub: "Freshly delivered · Newly arrived",
    tagline: "Tonight's recommendations.",
    to: "/arrivals",
    // Gemini-generated gothic atelier — twin gothic windows over the Aegean,
    // black-silk table laden with wax-sealed presents, a crimson rose
    img: "/api/files/ramshack/products/95dd0fc1a309478dbf101106899f1a73.png",
    size: "md",
    accent: "rgba(210,180,140,0.35)",
  },
  {
    key: "companions",
    name: "The Companion Wing",
    sub: "Always awake · AI Companions",
    tagline: "Someone who never sleeps.",
    to: "/shop/companions",
    // Gemini-generated gothic vanity — gilded gothic mirror opens onto a
    // starry crescent-moon sky, candles, decanter, ruby ring in velvet
    img: "/api/files/ramshack/products/281d7d80ad6a42718ce093d907da317f.png",
    size: "md",
    accent: "rgba(120,60,140,0.45)",
  },
  {
    key: "rooftop",
    name: "The Rooftop",
    sub: "Under the crescent · Date night",
    tagline: "Candelabras, wine, slow-burn kits.",
    to: "/shop/rooftop",
    // Gemini-generated gothic rooftop terrace — twin iron candelabras,
    // crescent moon over the Aegean, table for two with wine and figs
    img: "/api/files/ramshack/products/0ffafbedded443d3bd8fdd45ac46af85.png",
    size: "md",
    accent: "rgba(70,90,140,0.5)",
  },
];

const RoomCard = ({ room, engine }) => {
  const isLg = room.size === "lg";
  return (
    <Link
      data-testid={`room-card-${room.key}`}
      to={room.to}
      onMouseEnter={() => engine?.play(room.key)}
      onMouseLeave={() => engine?.stop()}
      className={`group relative overflow-hidden block ${
        isLg ? "md:col-span-2" : ""
      }`}
      style={{
        background: room.bg || "var(--rs-surface-2)",
      }}
    >
      {room.img && (
        <img
          src={room.img}
          alt={room.name}
          className="absolute inset-0 w-full h-full object-cover opacity-80 transition-all duration-[1100ms] ease-out group-hover:scale-[1.06] group-hover:opacity-100"
        />
      )}
      {/* Base fade */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,10,10,0.15) 0%, rgba(10,10,10,0.55) 55%, rgba(10,10,10,0.94) 100%)",
        }}
      />
      {/* Room-specific chromatic accent, revealed on hover */}
      <div
        className="absolute inset-0 opacity-40 group-hover:opacity-80 transition-opacity duration-700 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 100%, ${room.accent} 0%, rgba(0,0,0,0) 55%)`,
        }}
      />

      <div className={`absolute inset-0 flex flex-col justify-end p-6 md:p-8 ${isLg ? "md:p-12" : ""}`}>
        <div className="overline mb-2" style={{ color: "var(--rs-terracotta)" }}>
          {room.sub}
        </div>
        <div
          className={`font-serif-display text-bone leading-[1.05] mb-2 ${
            isLg ? "text-3xl md:text-5xl" : "text-2xl md:text-3xl"
          }`}
        >
          {room.name}
        </div>
        <p className="text-muted-bone text-xs md:text-sm italic mb-4 max-w-md">
          {room.tagline}
        </p>
        <div className="text-[10px] uppercase tracking-[0.32em] text-bone flex items-center gap-3">
          Step inside
          <span className="inline-block transition-transform duration-500 group-hover:translate-x-2">
            →
          </span>
        </div>
      </div>
    </Link>
  );
};

const Rooms = () => {
  const engineRef = useRef(null);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [left, setLeft] = useState(false);

  useEffect(() => {
    localStorage.setItem("rs_villa_entered", "1");
    window.scrollTo(0, 0);
    // Lazily create the sting engine — it stays quiet until a user actually
    // hovers a tile, so we don't burn an AudioContext on visitors who bounce.
    engineRef.current = createRoomStingEngine();
    return () => {
      engineRef.current?.destroy();
      engineRef.current = null;
    };
  }, []);

  const leaveAddress = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribing(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: email.trim() });
      setLeft(true);
      setEmail("");
      toast.success(
        data.message === "Already on the list"
          ? "You're already on the list."
          : "The house has your address.",
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not subscribe — check the address");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div
      data-testid="rooms-page"
      className="grain min-h-screen relative"
      style={{
        // Santorini villa view sits behind the entire floor plan — heavily
        // dimmed so the gothic room tiles pop and headline copy stays legible.
        backgroundImage:
          "linear-gradient(180deg, rgba(10,7,10,0.78) 0%, rgba(10,7,10,0.92) 60%, rgba(10,7,10,0.98) 100%), url('https://static.prod-images.emergentagent.com/jobs/6b746f73-141d-4e23-a891-20657f4c6e33/images/e809a07e3103e1da470d21e6d553e04bc3fa58edd64c7c19e6c9da9bf83f0f0f.png')",
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Prelude ribbon — overline and headline share a single row so the
          "Floor Plan" label reads like a room number stamped beside the title. */}
      <section
        className="px-6 md:px-12 lg:px-16 pt-10 md:pt-14 pb-8 border-b"
        style={{
          borderColor: "var(--rs-border)",
          background:
            "linear-gradient(180deg, rgba(35,15,20,0.6) 0%, rgba(10,10,10,0) 100%)",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-baseline md:gap-6 flex-wrap">
          <div
            className="overline shrink-0 md:pt-3"
            style={{ color: "var(--rs-terracotta)" }}
          >
            The Villa · Floor Plan
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-bone leading-[1.05]">
            You&apos;ve stepped inside. Now,{" "}
            <em className="not-italic" style={{ color: "var(--rs-terracotta)" }}>
              choose your room.
            </em>
          </h1>
        </div>
        <p className="text-muted-bone mt-4 text-sm sm:text-base leading-relaxed whitespace-normal md:whitespace-nowrap md:overflow-hidden md:text-ellipsis">
          Each door opens onto a different appetite. The villa remembers where you&apos;ve been — you can wander between rooms all night.
        </p>
      </section>

      {/* Bento floor plan — 4 columns × 2 rows so all seven rooms live above
          the fold. Bedroom occupies 2 columns to give the ocean-view suite the
          weight it deserves. */}
      <section className="px-6 md:px-12 lg:px-16 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-[240px] md:auto-rows-[260px]">
          {ROOMS.map((r) => (
            <RoomCard key={r.key} room={r} engine={engineRef.current} />
          ))}
        </div>
      </section>

      {/* A quiet moment on the way out — a folded note on the hall console.
          No velvet, no fanfare. Just a signature line waiting for an address. */}
      <section
        data-testid="rooms-newsletter"
        className="px-6 md:px-12 lg:px-16 pb-16 md:pb-24"
      >
        <div
          className="mx-auto max-w-3xl border-t border-b py-10 md:py-12"
          style={{ borderColor: "rgba(212,175,55,0.28)" }}
        >
          {left ? (
            <p
              data-testid="rooms-newsletter-success"
              className="text-center italic font-serif-display text-lg md:text-xl text-bone/90"
            >
              The house has your address. Watch the mail on the first of the month.
            </p>
          ) : (
            <form
              data-testid="rooms-newsletter-form"
              onSubmit={leaveAddress}
              className="flex flex-col md:flex-row md:items-end gap-6 md:gap-10"
            >
              <div className="flex-1 min-w-0">
                <div
                  className="overline mb-2"
                  style={{ color: "#d4af37", letterSpacing: "0.28em" }}
                >
                  A note left on the tray
                </div>
                <p className="font-serif-display italic text-bone text-xl md:text-2xl leading-snug">
                  Leave your address. The house writes once a month.
                </p>
              </div>
              <div className="flex items-end gap-4 md:w-[380px] md:shrink-0">
                <input
                  data-testid="rooms-newsletter-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@address.com"
                  required
                  aria-label="Your email address"
                  className="flex-1 bg-transparent border-0 border-b outline-none py-2 text-sm text-bone placeholder:text-muted-bone/60 focus:border-b-2 transition"
                  style={{ borderBottomColor: "rgba(212,175,55,0.55)" }}
                />
                <button
                  data-testid="rooms-newsletter-submit"
                  type="submit"
                  disabled={subscribing || !email.trim()}
                  className="rs-tray-submit uppercase tracking-[0.28em] text-[11px] pb-2 disabled:opacity-40"
                >
                  {subscribing ? "…" : "Leave it"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <style>{`
        .rs-tray-submit {
          color: #d4af37;
          background: transparent;
          border: none;
          cursor: pointer;
          border-bottom: 1px solid rgba(212,175,55,0.6);
          padding-left: 2px;
          padding-right: 2px;
          transition: color 200ms ease, border-bottom-color 200ms ease, letter-spacing 300ms ease;
        }
        .rs-tray-submit:hover:not(:disabled) {
          color: #f0d38a;
          border-bottom-color: rgba(240,211,138,0.9);
          letter-spacing: 0.32em;
        }
        .rs-tray-submit:disabled { cursor: not-allowed; }
      `}</style>
    </div>
  );
};

export default Rooms;
